/**
 * Concurrency-cap status panel (cost_ranked_model_routing_rebuild_2026_08_28.md
 * Phase 2 "dashboard surfacing" evidence bar). Renders GET /api/accounts/concurrency-caps
 * verbatim -- one row per server/concurrency_caps.py CapStatus reading (per-account
 * GLM/Anthropic caps, plus the pre-existing fleet-wide/per-project Gemini/Muse caps
 * surfaced read-only alongside them). Both verdicts are the backend's own, never
 * re-derived client-side: `at_cap` (`active >= cap`) means no headroom is left, which
 * for a repo or account is the normal steady state, and `over_cap` (`active > cap`)
 * means the ceiling was genuinely breached. Only `over_cap` is an alarm condition; a
 * full cap is what the cap is FOR. The gauge width below is the only locally-computed
 * number, purely visual. Mirrors GeminiCapacityPanel/NvidiaCapacityPanel's shape
 * (poll + Panel + per-row gauge table).
 *
 * The same reading, scoped to one slot's bound account
 * (GET /api/slots/{slot_id}/concurrency-cap), has no hook consumer since the sub-agent
 * fan-out guard dropped its cap check (2026-09-24) -- api.getSlotConcurrencyCap is wired for any future
 * slot-scoped consumer, but this fleet-wide panel has no per-slot context of its own
 * to call it with.
 */

import { useEffect, useState } from "react";

import { api } from "./api";
import { Panel } from "./components";
import type { ConcurrencyCapView } from "./types";

const POLL_INTERVAL_MS = 30000;

const SCOPE_LABEL: Record<string, string> = {
  account: "per-account",
  fleet: "fleet-wide",
  project: "per-project",
  repo: "per-repo",
};

export function usagePct(active: number, cap: number): number {
  return cap <= 0 ? 100 : Math.min(100, (active / cap) * 100);
}

function GaugeBar({ active, cap, atCap, overCap }: { active: number; cap: number; atCap: boolean; overCap: boolean }) {
  const pct = usagePct(active, cap);
  // Red is reserved for a genuine breach; a cap that is merely FULL is a warning, not
  // an alarm -- otherwise every habitually-full repo (AO runs at 8/8 routinely) would
  // paint the panel red permanently.
  const tone = overCap
    ? "capacity-gauge-fill-danger"
    : atCap || pct >= 60
      ? "capacity-gauge-fill-warn"
      : "capacity-gauge-fill-ok";
  return (
    <div className="capacity-gauge-track">
      <div className={`capacity-gauge-fill ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function ConcurrencyCapsPanel({ baseUrl }: { baseUrl: string }) {
  const [rows, setRows] = useState<ConcurrencyCapView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.getConcurrencyCaps(baseUrl);
        if (!cancelled) {
          setRows(result);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  const overCapCount = rows?.filter((r) => r.over_cap).length ?? 0;
  const atCapCount = rows?.filter((r) => r.at_cap && !r.over_cap).length ?? 0;

  return (
    <Panel title="Concurrency Caps" collapsible tone="accounts">
      <div className="usage-panel-hint">
        Per-account (GLM/Anthropic), fleet/project (Gemini/Muse), and per-repo concurrency ceilings enforced at dispatch
        (server/concurrency_caps.py) and independently by the sub-agent spawn pre-hook. Per-repo rows mirror
        dispatch.py's repo-collision guard (_repo_collision_max_concurrent_per_repo) — "Active" there counts only
        working/blocked slots whose claim is not stale (older than 90min AND silent past the fleet's heartbeat budget),
        so an idle/killed/paused/repo_contention slot never counts against it, while a live worker parked in a long gate
        wait always does. The other rows' "Active" counts SlotRows in working/dispatched/stale — a worker's own nested
        sub-agent forks are not separately visible here.
      </div>
      {overCapCount > 0 && (
        <div className="concurrency-cap-over-banner">
          ⚠️{" "}
          <strong>
            {overCapCount} cap{overCapCount === 1 ? "" : "s"} over limit
          </strong>
        </div>
      )}
      {overCapCount === 0 && atCapCount > 0 && (
        <div className="usage-panel-hint">
          {atCapCount} cap{atCapCount === 1 ? "" : "s"} at limit (full, not breached).
        </div>
      )}
      {error && <div className="task-usage-error">Failed to load: {error}</div>}
      {!error && !rows && <div className="task-usage-pending">loading…</div>}
      {!error && rows && rows.length === 0 && <div className="task-usage-pending">no concurrency caps configured</div>}
      {!error && rows && rows.length > 0 && (
        <div className="concurrency-caps-groups">
          {rows.map((r) => (
            <div key={`${r.scope}:${r.key}`} className="capacity-account">
              <div className="capacity-account-header">
                <span className="mono">{r.key}</span>
                <span className="capacity-account-meta">
                  {r.provider ? `${r.provider} · ` : ""}
                  {SCOPE_LABEL[r.scope] ?? r.scope}
                  {r.over_cap ? " · over cap" : r.at_cap ? " · at cap" : ""}
                </span>
              </div>
              <table className="acc-usage-table acc-usage-table-breakdown">
                <tbody>
                  <tr>
                    <td className="acc-usage-window-label">Active</td>
                    <td className="mono">
                      {r.active} / {r.cap}
                    </td>
                    <td>
                      <GaugeBar active={r.active} cap={r.cap} atCap={r.at_cap} overCap={r.over_cap} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
